/**
 * Host Global - Property Rental Management System
 * Super Admin - Inspection Review Detail Page
 * Dedicated page for reviewing a specific inspection and making approval/rejection decision
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import CircularProgress from "@mui/material/CircularProgress";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import InputAdornment from "@mui/material/InputAdornment";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

// Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// API
import { superAdminAPI, adminAPI } from "utils/api";

// Icons
import Icon from "@mui/material/Icon";

function InspectionReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(null); // "approve" or "reject"
  const [notes, setNotes] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [price, setPrice] = useState("");
  const [profitPercentage, setProfitPercentage] = useState("");
  const [insurancePrice, setInsurancePrice] = useState("");
  const [maintenanceCostPercentage, setMaintenanceCostPercentage] = useState("");
  const [maxGuests, setMaxGuests] = useState("");
  const [extraFeesPerPerson, setExtraFeesPerPerson] = useState("");
  const [earlyCheckInFeePercentage, setEarlyCheckInFeePercentage] = useState("");
  const [lateCheckOutFeePercentage, setLateCheckOutFeePercentage] = useState("");
  const [checkInOutAgentId, setCheckInOutAgentId] = useState("");
  const [checkInOutAgents, setCheckInOutAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [expectedReadyDate, setExpectedReadyDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [detailsTab, setDetailsTab] = useState(0);
  const [missingItemsDialogOpen, setMissingItemsDialogOpen] = useState(false);
  const [missingItemsData, setMissingItemsData] = useState({
    items: [{ name: "", quantity: 1, price: "" }],
    expectedDate: "",
    expectedDateApprovalToListing: "",
    customMessage: "",
    pricePerDay: "",
    occupancyRate: "",
    companyPercentage: "",
    bookingWebsitePercentage: "",
  });
  const [submittingMissingItems, setSubmittingMissingItems] = useState(false);
  const [approveToOfferDialogOpen, setApproveToOfferDialogOpen] = useState(false);
  const [submittingApproveToOffer, setSubmittingApproveToOffer] = useState(false);
  const [criteriaFilter, setCriteriaFilter] = useState("");

  useEffect(() => {
    fetchPropertyDetails();
  }, [id]);

  const fetchPropertyDetails = async () => {
    try {
      setLoading(true);
      setMessage({ type: "", text: "" });

      const response = await superAdminAPI.getPropertyById(id);
      setProperty(response.data || response.property);
    } catch (error) {
      console.error("Error fetching property details:", error);
      setMessage({
        type: "error",
        text: "Failed to load inspection details. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAction = async (type) => {
    setActionType(type);
    setNotes("");
    setRejectReason("");
    setPrice("");
    setProfitPercentage("");
    setInsurancePrice("");
    setMaintenanceCostPercentage("");
    setMaxGuests("");
    setExtraFeesPerPerson("");
    setEarlyCheckInFeePercentage("");
    setCheckInOutAgentId("");
    setExpectedReadyDate("");
    if (type === "approveToOffer") {
      setApproveToOfferDialogOpen(true);
      return;
    }
    if (type === "approve") {
      await fetchCheckInOutAgents();
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setExpectedReadyDate(tomorrow.toISOString().split("T")[0]);
    }
    setActionDialogOpen(true);
  };

  const handleApproveToInspectionOffer = async () => {
    if (!property) return;
    try {
      setSubmittingApproveToOffer(true);
      setMessage({ type: "", text: "" });
      const response = await superAdminAPI.approveToInspectionOffer(property._id || property.id);
      if (response.success !== false) {
        setMessage({
          type: "success",
          text:
            response.message ||
            "Property moved to Inspection Offer. Draft contract will be sent to the owner.",
        });
        setApproveToOfferDialogOpen(false);
        setTimeout(() => navigate("/super-admin/inspections"), 1500);
      }
    } catch (error) {
      setMessage({
        type: "error",
        text: error.message || "Failed to approve to inspection offer.",
      });
    } finally {
      setSubmittingApproveToOffer(false);
    }
  };

  const handleSendMissingItems = async () => {
    if (!property) return;
    const validItems = (missingItemsData.items || [])
      .filter(
        (item) =>
          (item.name && String(item.name).trim()) ||
          (item.quantity != null && item.quantity !== "") ||
          (item.price != null && item.price !== "")
      )
      .map((item) => ({
        name: String(item.name || "").trim() || "Item",
        quantity: Math.max(1, parseInt(item.quantity, 10) || 1),
        price: parseFloat(item.price) || 0,
      }));
    if (validItems.length === 0) {
      setMessage({ type: "error", text: "Add at least one item with a name." });
      return;
    }
    try {
      setSubmittingMissingItems(true);
      setMessage({ type: "", text: "" });
      await superAdminAPI.sendMissingItemsEmail(property._id || property.id, {
        items: validItems,
        expectedDate: missingItemsData.expectedDate || undefined,
        expectedDateApprovalToListing: missingItemsData.expectedDateApprovalToListing || undefined,
        customMessage: missingItemsData.customMessage?.trim() || undefined,
        pricePerDay: missingItemsData.pricePerDay
          ? parseFloat(missingItemsData.pricePerDay)
          : undefined,
        occupancyRate: missingItemsData.occupancyRate
          ? parseFloat(missingItemsData.occupancyRate)
          : undefined,
        companyPercentage: missingItemsData.companyPercentage
          ? parseFloat(missingItemsData.companyPercentage)
          : undefined,
        bookingWebsitePercentage: missingItemsData.bookingWebsitePercentage
          ? parseFloat(missingItemsData.bookingWebsitePercentage)
          : undefined,
      });
      setMessage({
        type: "success",
        text: "Missing items email sent successfully (PDF attached).",
      });
      setMissingItemsDialogOpen(false);
      setMissingItemsData({
        items: [{ name: "", quantity: 1, price: "" }],
        expectedDate: "",
        expectedDateApprovalToListing: "",
        customMessage: "",
        pricePerDay: "",
        occupancyRate: "",
        companyPercentage: "",
        bookingWebsitePercentage: "",
      });
    } catch (error) {
      setMessage({ type: "error", text: error.message || "Failed to send missing items email." });
    } finally {
      setSubmittingMissingItems(false);
    }
  };

  const fetchCheckInOutAgents = async () => {
    try {
      setLoadingAgents(true);
      const response = await adminAPI.getAvailableCheckInOutAgents();
      setCheckInOutAgents(response.agents || []);
    } catch (error) {
      console.error("Error fetching check-in/check-out agents:", error);
      setMessage({
        type: "error",
        text: "Failed to load check-in/check-out agents. You can still approve without assigning an agent.",
      });
    } finally {
      setLoadingAgents(false);
    }
  };

  const handleSubmitAction = async () => {
    if (!property) return;

    if (actionType === "reject" && !rejectReason.trim()) {
      setMessage({
        type: "error",
        text: "Rejection reason is required.",
      });
      return;
    }

    const isSecondInspection = property.status === "Second_Inspection_Completed";
    if (actionType === "approve" && !isSecondInspection) {
      if (!price || parseFloat(price) <= 0) {
        setMessage({
          type: "error",
          text: "Price per night is required and must be greater than 0.",
        });
        return;
      }

      if (
        !profitPercentage ||
        parseFloat(profitPercentage) < 0 ||
        parseFloat(profitPercentage) > 100
      ) {
        setMessage({
          type: "error",
          text: "Profit percentage is required and must be between 0 and 100.",
        });
        return;
      }

      if (!checkInOutAgentId) {
        setMessage({
          type: "error",
          text: "Check-in/check-out agent is required to approve and list a property.",
        });
        return;
      }

      // Expected ready date is optional; if provided, validate it's not in the past
      if (expectedReadyDate) {
        const selectedDate = new Date(expectedReadyDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        selectedDate.setHours(0, 0, 0, 0);
        if (selectedDate < today) {
          setMessage({
            type: "error",
            text: "Expected ready date must be today or in the future.",
          });
          return;
        }
      }
    }

    try {
      setSubmitting(true);
      setMessage({ type: "", text: "" });

      let response;
      if (actionType === "approve") {
        if (isSecondInspection) {
          response = await superAdminAPI.approveSecondInspection(property._id || property.id);
        } else {
          response = await superAdminAPI.approveProperty(
            property._id || property.id,
            parseFloat(price),
            parseFloat(profitPercentage),
            notes.trim() || undefined,
            insurancePrice !== "" ? parseFloat(insurancePrice) : undefined,
            checkInOutAgentId || undefined,
            expectedReadyDate || undefined,
            maintenanceCostPercentage !== "" ? parseFloat(maintenanceCostPercentage) : undefined,
            maxGuests !== "" ? maxGuests : undefined,
            extraFeesPerPerson !== "" ? parseFloat(extraFeesPerPerson) : undefined,
            earlyCheckInFeePercentage !== "" ? parseFloat(earlyCheckInFeePercentage) : undefined,
            lateCheckOutFeePercentage !== "" ? parseFloat(lateCheckOutFeePercentage) : undefined
          );
        }
      } else {
        response = await superAdminAPI.rejectProperty(
          property._id || property.id,
          rejectReason.trim()
        );
      }

      if (response.success) {
        setMessage({
          type: "success",
          text: `Property ${actionType === "approve" ? "approved" : "rejected"} successfully.`,
        });
        setActionDialogOpen(false);
        // Redirect back to inspections list after a short delay
        setTimeout(() => {
          navigate("/super-admin/inspections");
        }, 1500);
      }
    } catch (error) {
      console.error(`Error ${actionType}ing property:`, error);
      setMessage({
        type: "error",
        text: error.message || `Failed to ${actionType} property. Please try again.`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Approved":
        return "success";
      case "Pending":
        return "warning";
      case "Inspection":
        return "info";
      case "Inspection_Completed":
        return "info";
      case "Rejected":
        return "error";
      default:
        return "secondary";
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3} display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (!property) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3}>
          <Alert severity="error">Property not found or inspection not available for review.</Alert>
          <MDBox mt={2}>
            <MDButton
              variant="gradient"
              color="info"
              onClick={() => navigate("/super-admin/inspections")}
            >
              <Icon sx={{ mr: 1 }}>arrow_back</Icon>
              Back to Inspections
            </MDButton>
          </MDBox>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Breadcrumbs */}
        <MDBox mb={3}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link
              component="button"
              variant="body2"
              color="inherit"
              onClick={() => navigate("/super-admin/inspections")}
              sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
            >
              Inspections Review
            </Link>
            <MDTypography variant="body2" color="text">
              Review Details
            </MDTypography>
          </Breadcrumbs>
        </MDBox>

        {/* Header */}
        <MDBox mb={3} display="flex" justifyContent="space-between" alignItems="flex-start">
          <MDBox>
            <MDTypography variant="h4" fontWeight="bold" mb={1}>
              Inspection
            </MDTypography>
            <MDTypography variant="body2" color="text">
              Review inspection details and make approval decision
            </MDTypography>
          </MDBox>
          <MDBox display="flex" gap={1} flexWrap="wrap">
            <MDButton
              variant="outlined"
              color="info"
              onClick={() => navigate("/super-admin/inspections")}
              startIcon={<Icon>arrow_back</Icon>}
            >
              Back
            </MDButton>
            {property && property.status === "Inspection_Completed" && (
              <>
                <MDButton
                  variant="outlined"
                  color="warning"
                  onClick={() => setMissingItemsDialogOpen(true)}
                  startIcon={<Icon>email</Icon>}
                >
                  Send Missing Items Email
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="error"
                  onClick={() => handleOpenAction("reject")}
                  startIcon={<Icon>close</Icon>}
                >
                  Reject
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="success"
                  onClick={() => handleOpenAction("approveToOffer")}
                  startIcon={<Icon>check</Icon>}
                >
                  Approve to Inspection Offer
                </MDButton>
              </>
            )}
            {property && property.status === "Second_Inspection_Completed" && (
              <>
                <MDButton
                  variant="outlined"
                  color="warning"
                  onClick={() => setMissingItemsDialogOpen(true)}
                  startIcon={<Icon>email</Icon>}
                >
                  Send Missing Items Email
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="error"
                  onClick={() => handleOpenAction("reject")}
                  startIcon={<Icon>close</Icon>}
                >
                  Reject
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="success"
                  onClick={() => handleOpenAction("approve")}
                  startIcon={<Icon>check</Icon>}
                >
                  Approve
                </MDButton>
              </>
            )}
          </MDBox>
        </MDBox>

        {/* Message Alert */}
        {message.text && (
          <MDBox mb={3}>
            <Alert severity={message.type} onClose={() => setMessage({ type: "", text: "" })}>
              {message.text}
            </Alert>
          </MDBox>
        )}

        {/* Property Overview Card */}
        <Card sx={{ mb: 3, overflow: "hidden" }}>
          <Grid container>
            {/* Property Image */}
            <Grid item xs={12} md={4}>
              {property.images && property.images.length > 0 ? (
                <MDBox
                  height="100%"
                  minHeight="300px"
                  sx={{
                    backgroundImage: `url(${property.images[0]})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
              ) : (
                <MDBox
                  height="100%"
                  minHeight="300px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bgcolor="grey.200"
                >
                  <Icon sx={{ fontSize: "4rem", color: "grey.400" }}>home</Icon>
                </MDBox>
              )}
            </Grid>

            {/* Property Info */}
            <Grid item xs={12} md={8}>
              <MDBox p={3}>
                <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <MDBox>
                    <MDTypography variant="h5" fontWeight="bold" mb={1}>
                      {property.title?.en || property.title || "Untitled Property"}
                    </MDTypography>
                    {property.title?.ar && (
                      <MDTypography
                        variant="h5"
                        fontWeight="medium"
                        color="text.secondary"
                        mb={1}
                        sx={{ direction: "rtl", textAlign: "right" }}
                      >
                        {property.title.ar}
                      </MDTypography>
                    )}
                    <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                      <Icon fontSize="small" color="text.secondary">
                        location_on
                      </Icon>
                      <MDTypography variant="body2" color="text">
                        {property.address?.en || property.address || "N/A"}
                        {property.city?.en || property.city
                          ? `, ${property.city?.en || property.city}`
                          : ""}
                        {property.country?.en || property.country
                          ? `, ${property.country?.en || property.country}`
                          : ""}
                      </MDTypography>
                    </MDBox>
                    {(property.address?.ar || property.city?.ar || property.country?.ar) && (
                      <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                        <Icon fontSize="small" color="text.secondary">
                          location_on
                        </Icon>
                        <MDTypography
                          variant="body2"
                          color="text.secondary"
                          sx={{ direction: "rtl", textAlign: "right" }}
                        >
                          {property.address?.ar || ""}
                          {property.city?.ar ? `، ${property.city.ar}` : ""}
                          {property.country?.ar ? `، ${property.country.ar}` : ""}
                        </MDTypography>
                      </MDBox>
                    )}
                  </MDBox>
                  <Chip
                    label={property.status?.replace(/_/g, " ") || "Unknown"}
                    color={getStatusColor(property.status)}
                    size="medium"
                  />
                </MDBox>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={6} sm={4}>
                    <MDBox>
                      <MDTypography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        mb={0.5}
                      >
                        Property Type
                      </MDTypography>
                      <MDTypography variant="body1" fontWeight="medium">
                        {property.propertyType
                          ? property.propertyType.charAt(0).toUpperCase() +
                            property.propertyType.slice(1)
                          : "N/A"}
                      </MDTypography>
                    </MDBox>
                  </Grid>
                  {property.rooms && (
                    <Grid item xs={6} sm={4}>
                      <MDBox>
                        <MDTypography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          mb={0.5}
                        >
                          Rooms
                        </MDTypography>
                        <MDTypography variant="body1" fontWeight="medium">
                          {property.rooms}
                        </MDTypography>
                      </MDBox>
                    </Grid>
                  )}
                  {property.bathrooms && (
                    <Grid item xs={6} sm={4}>
                      <MDBox>
                        <MDTypography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          mb={0.5}
                        >
                          Bathrooms
                        </MDTypography>
                        <MDTypography variant="body1" fontWeight="medium">
                          {property.bathrooms}
                        </MDTypography>
                      </MDBox>
                    </Grid>
                  )}
                  {property.assignedInspector && (
                    <Grid item xs={6} sm={4}>
                      <MDBox>
                        <MDTypography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          mb={0.5}
                        >
                          Inspector
                        </MDTypography>
                        <MDTypography variant="body1" fontWeight="medium">
                          {property.assignedInspector.fullName || "N/A"}
                        </MDTypography>
                      </MDBox>
                    </Grid>
                  )}
                  {property.inspectionFindings?.inspectedAt && (
                    <Grid item xs={6} sm={4}>
                      <MDBox>
                        <MDTypography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          mb={0.5}
                        >
                          Inspected At
                        </MDTypography>
                        <MDTypography variant="body1" fontWeight="medium">
                          {formatDate(property.inspectionFindings.inspectedAt)}
                        </MDTypography>
                      </MDBox>
                    </Grid>
                  )}
                </Grid>
              </MDBox>
            </Grid>
          </Grid>
        </Card>

        {/* Tabs for Detailed Information */}
        <Card>
          <MDBox p={3}>
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
              <Tabs value={detailsTab} onChange={(e, newValue) => setDetailsTab(newValue)}>
                <Tab label="Inspection Findings" />
                <Tab
                  label={`Photos (${property.inspectionFindings?.inspectionPhotos?.length || 0})`}
                />
                <Tab label="Property Details" />
                <Tab label="Criteria Verification" />
              </Tabs>
            </Box>

            {/* Inspection Findings Tab */}
            {detailsTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Card
                    sx={{
                      p: 3,
                      mb: 2,
                      borderRadius: 2,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                    }}
                  >
                    <MDBox
                      display="flex"
                      alignItems="center"
                      mb={3}
                      p={2}
                      borderRadius={2}
                      sx={{
                        background:
                          "linear-gradient(135deg, rgba(33, 150, 243, 0.08) 0%, rgba(33, 150, 243, 0.03) 100%)",
                        borderLeft: "4px solid",
                        borderColor: "info.main",
                      }}
                    >
                      <MDBox
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1.5,
                          bgcolor: "grey.200",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mr: 2,
                        }}
                      >
                        <Icon sx={{ color: "text.secondary", fontSize: "1.5rem" }}>assignment</Icon>
                      </MDBox>
                      <MDBox>
                        <MDTypography variant="h6" fontWeight="bold">
                          Inspection Information
                        </MDTypography>
                        <MDTypography variant="caption" color="text.secondary">
                          Inspector assignment and inspection details
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <MDBox
                          mb={2}
                          p={2}
                          borderRadius={2}
                          sx={{
                            bgcolor: "grey.50",
                            border: "1px solid",
                            borderColor: "divider",
                          }}
                        >
                          <MDBox display="flex" alignItems="center" mb={1}>
                            <Icon sx={{ fontSize: "1rem", mr: 1, color: "text.secondary" }}>
                              person
                            </Icon>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                            >
                              Inspector
                            </MDTypography>
                          </MDBox>
                          <MDTypography variant="body1" fontWeight="medium" sx={{ pl: 4 }}>
                            {property.assignedInspector?.fullName || "N/A"}
                          </MDTypography>
                          {property.assignedInspector?.email && (
                            <MDTypography
                              variant="body2"
                              component="a"
                              href={`mailto:${property.assignedInspector.email}`}
                              sx={{
                                pl: 4,
                                color: "primary.main",
                                textDecoration: "none",
                                "&:hover": {
                                  textDecoration: "underline",
                                },
                              }}
                            >
                              {property.assignedInspector.email}
                            </MDTypography>
                          )}
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <MDBox
                          mb={2}
                          p={2}
                          borderRadius={2}
                          sx={{
                            bgcolor: "grey.50",
                            border: "1px solid",
                            borderColor: "divider",
                          }}
                        >
                          <MDBox display="flex" alignItems="center" mb={1}>
                            <Icon sx={{ fontSize: "1rem", mr: 1, color: "text.secondary" }}>
                              event
                            </Icon>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                            >
                              Inspection Date
                            </MDTypography>
                          </MDBox>
                          <MDTypography variant="body1" fontWeight="medium" sx={{ pl: 4 }}>
                            {formatDate(property.inspectionDate)}
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <MDBox mb={2}>
                          <MDBox display="flex" alignItems="center" mb={1}>
                            <Icon sx={{ fontSize: "1rem", mr: 1, color: "text.secondary" }}>
                              location_on
                            </Icon>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                            >
                              Inspection Location
                            </MDTypography>
                          </MDBox>
                          <MDTypography variant="body1" mb={1.5} sx={{ pl: 4 }}>
                            {property.inspectionLocation?.replace(/Google Maps:.*$/i, "").trim() ||
                              property.inspectionLocation ||
                              "N/A"}
                          </MDTypography>
                          {property.inspectionLocation && (
                            <MDButton
                              variant="gradient"
                              color="error"
                              size="small"
                              startIcon={<Icon>map</Icon>}
                              onClick={() => {
                                const location =
                                  property.inspectionLocation ||
                                  property.address?.en ||
                                  property.address ||
                                  "";
                                const cleanLocation = location
                                  .replace(/Google Maps:.*$/i, "")
                                  .trim();
                                const googleMapsUrl =
                                  property.googleMapLink ||
                                  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                                    cleanLocation
                                  )}`;
                                window.open(googleMapsUrl, "_blank", "noopener,noreferrer");
                              }}
                              sx={{
                                ml: 4,
                                boxShadow: "0 4px 12px rgba(244, 67, 54, 0.3)",
                                "&:hover": {
                                  boxShadow: "0 6px 16px rgba(244, 67, 54, 0.4)",
                                },
                              }}
                            >
                              Open in Google Maps
                            </MDButton>
                          )}
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <MDBox mb={2}>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            mb={0.5}
                          >
                            Inspected At
                          </MDTypography>
                          <MDTypography variant="body1" fontWeight="medium">
                            {formatDate(property.inspectionFindings?.inspectedAt)}
                          </MDTypography>
                        </MDBox>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>

                {property.inspectionFindings && (
                  <>
                    {/* Verified Amenities */}
                    {property.inspectionFindings.amenitiesVerified &&
                      property.inspectionFindings.amenitiesVerified.length > 0 && (
                        <Grid item xs={12} md={6}>
                          <Card sx={{ p: 2, height: "100%" }}>
                            <MDTypography
                              variant="subtitle2"
                              fontWeight="bold"
                              mb={1.5}
                              color="success.main"
                            >
                              Verified Amenities
                            </MDTypography>
                            <MDBox display="flex" gap={1} flexWrap="wrap">
                              {property.inspectionFindings.amenitiesVerified.map(
                                (amenity, index) => {
                                  const amenityLabel =
                                    typeof amenity === "object" && amenity.en
                                      ? amenity.en
                                      : typeof amenity === "string"
                                      ? amenity
                                      : "";
                                  return (
                                    <Chip
                                      key={index}
                                      label={amenityLabel}
                                      color="success"
                                      size="small"
                                      icon={<Icon>check_circle</Icon>}
                                    />
                                  );
                                }
                              )}
                            </MDBox>
                          </Card>
                        </Grid>
                      )}

                    {/* Missing Amenities */}
                    {property.inspectionFindings.missingAmenities &&
                      property.inspectionFindings.missingAmenities.length > 0 && (
                        <Grid item xs={12} md={6}>
                          <Card sx={{ p: 2, height: "100%" }}>
                            <MDTypography
                              variant="subtitle2"
                              fontWeight="bold"
                              mb={1.5}
                              color="error.main"
                            >
                              Missing Amenities
                            </MDTypography>
                            <MDBox display="flex" gap={1} flexWrap="wrap">
                              {property.inspectionFindings.missingAmenities.map(
                                (amenity, index) => {
                                  const amenityLabel =
                                    typeof amenity === "object" && amenity.en
                                      ? amenity.en
                                      : typeof amenity === "string"
                                      ? amenity
                                      : "";
                                  return (
                                    <Chip
                                      key={index}
                                      label={amenityLabel}
                                      color="error"
                                      size="small"
                                      icon={<Icon>cancel</Icon>}
                                    />
                                  );
                                }
                              )}
                            </MDBox>
                          </Card>
                        </Grid>
                      )}

                    {/* Additional Notes */}
                    {property.inspectionFindings.additionalNotes && (
                      <Grid item xs={12}>
                        <Card sx={{ p: 2 }}>
                          <MDTypography variant="subtitle2" fontWeight="bold" mb={1.5}>
                            Inspector Notes
                          </MDTypography>
                          <MDTypography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                            {property.inspectionFindings.additionalNotes}
                          </MDTypography>
                        </Card>
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            )}

            {/* Inspection Photos Tab */}
            {detailsTab === 1 && (
              <MDBox>
                {!property.inspectionFindings?.inspectionPhotos ||
                property.inspectionFindings.inspectionPhotos.length === 0 ? (
                  <MDBox textAlign="center" py={6}>
                    <Icon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}>image</Icon>
                    <MDTypography variant="h6" color="text.secondary" mb={1}>
                      No Photos Available
                    </MDTypography>
                    <MDTypography variant="body2" color="text.secondary">
                      No inspection photos were uploaded.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <Grid container spacing={2}>
                    {property.inspectionFindings.inspectionPhotos.map((photoUrl, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Paper
                          elevation={2}
                          sx={{
                            borderRadius: 2,
                            overflow: "hidden",
                            "&:hover": {
                              boxShadow: 4,
                            },
                          }}
                        >
                          <MDBox
                            component="img"
                            src={photoUrl}
                            alt={`Inspection photo ${index + 1}`}
                            sx={{
                              width: "100%",
                              height: "250px",
                              objectFit: "cover",
                              cursor: "pointer",
                            }}
                            onClick={() => window.open(photoUrl, "_blank")}
                          />
                          <MDBox p={1.5}>
                            <MDTypography variant="caption" color="text.secondary">
                              Photo {index + 1}
                            </MDTypography>
                          </MDBox>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </MDBox>
            )}

            {/* Property Details Tab */}
            {detailsTab === 2 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card sx={{ p: 2 }}>
                    <MDTypography
                      variant="subtitle2"
                      fontWeight="bold"
                      mb={2}
                      color="text.secondary"
                    >
                      Basic Information
                    </MDTypography>
                    <MDBox mb={1.5}>
                      <MDTypography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        mb={0.5}
                      >
                        Title
                      </MDTypography>
                      <MDTypography variant="body1" fontWeight="medium">
                        {property.title?.en || property.title || "N/A"}
                      </MDTypography>
                    </MDBox>
                    <MDBox mb={1.5}>
                      <MDTypography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        mb={0.5}
                      >
                        Type
                      </MDTypography>
                      <MDTypography variant="body1">
                        {property.propertyType
                          ? property.propertyType.charAt(0).toUpperCase() +
                            property.propertyType.slice(1)
                          : "N/A"}
                      </MDTypography>
                    </MDBox>
                    <MDBox mb={1.5}>
                      <MDTypography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        mb={0.5}
                      >
                        Address
                      </MDTypography>
                      <MDTypography variant="body1">
                        {property.address?.en || property.address || "N/A"}
                        {(property.city?.en || property.city) &&
                          `, ${property.city?.en || property.city}`}
                        {(property.country?.en || property.country) &&
                          `, ${property.country?.en || property.country}`}
                      </MDTypography>
                    </MDBox>
                    <MDBox display="flex" gap={2} flexWrap="wrap">
                      {property.rooms && (
                        <MDBox>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            mb={0.5}
                          >
                            Rooms
                          </MDTypography>
                          <MDTypography variant="body1" fontWeight="medium">
                            {property.rooms}
                          </MDTypography>
                        </MDBox>
                      )}
                      {property.bathrooms && (
                        <MDBox>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            mb={0.5}
                          >
                            Bathrooms
                          </MDTypography>
                          <MDTypography variant="body1" fontWeight="medium">
                            {property.bathrooms}
                          </MDTypography>
                        </MDBox>
                      )}
                      {property.size && (
                        <MDBox>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            mb={0.5}
                          >
                            Size
                          </MDTypography>
                          <MDTypography variant="body1" fontWeight="medium">
                            {property.size} sq ft
                          </MDTypography>
                        </MDBox>
                      )}
                    </MDBox>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card sx={{ p: 2 }}>
                    <MDTypography
                      variant="subtitle2"
                      fontWeight="bold"
                      mb={2}
                      color="text.secondary"
                    >
                      Owner Information
                    </MDTypography>
                    {property.owner ? (
                      <>
                        <MDBox mb={1.5}>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            mb={0.5}
                          >
                            Name
                          </MDTypography>
                          <MDTypography variant="body1" fontWeight="medium">
                            {property.owner.fullName || "N/A"}
                          </MDTypography>
                        </MDBox>
                        <MDBox mb={1.5}>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            mb={0.5}
                          >
                            Email
                          </MDTypography>
                          <MDTypography variant="body1">
                            {property.owner.email || "N/A"}
                          </MDTypography>
                        </MDBox>
                        {property.owner.phone && (
                          <MDBox>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              mb={0.5}
                            >
                              Phone
                            </MDTypography>
                            <MDTypography variant="body1">{property.owner.phone}</MDTypography>
                          </MDBox>
                        )}
                      </>
                    ) : (
                      <MDTypography variant="body2" color="text.secondary">
                        Owner information not available
                      </MDTypography>
                    )}
                  </Card>
                </Grid>

                {property.description && (
                  <Grid item xs={12}>
                    <Card sx={{ p: 2 }}>
                      <MDTypography
                        variant="subtitle2"
                        fontWeight="bold"
                        mb={1}
                        color="text.secondary"
                      >
                        Description
                      </MDTypography>
                      <MDTypography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                        {property.description?.en ||
                          property.description ||
                          "No description provided"}
                      </MDTypography>
                    </Card>
                  </Grid>
                )}

                {property.amenities && property.amenities.length > 0 && (
                  <Grid item xs={12}>
                    <Card sx={{ p: 2 }}>
                      <MDTypography
                        variant="subtitle2"
                        fontWeight="bold"
                        mb={1.5}
                        color="text.secondary"
                      >
                        Listed Amenities
                      </MDTypography>
                      <MDBox display="flex" gap={1} flexWrap="wrap">
                        {property.amenities.map((amenity, index) => {
                          const amenityLabel =
                            typeof amenity === "object" && amenity.en
                              ? amenity.en
                              : typeof amenity === "string"
                              ? amenity
                              : "";
                          return (
                            <Chip
                              key={index}
                              label={amenityLabel}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          );
                        })}
                      </MDBox>
                    </Card>
                  </Grid>
                )}
              </Grid>
            )}

            {/* Criteria Verification Tab */}
            {detailsTab === 3 && (
              <MDBox>
                {property.criteria && property.criteria.length > 0 ? (
                  <Card sx={{ p: 3 }}>
                    <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
                      <MDBox display="flex" alignItems="center">
                        <Icon sx={{ mr: 1, color: "info.main" }}>checklist</Icon>
                        <MDTypography variant="h6" fontWeight="bold">
                          Property Criteria Verification
                        </MDTypography>
                      </MDBox>
                      <TextField
                        size="small"
                        placeholder="Filter criteria by name or category..."
                        value={criteriaFilter}
                        onChange={(e) => setCriteriaFilter(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon>search</Icon>
                            </InputAdornment>
                          ),
                        }}
                        sx={{ minWidth: 280 }}
                      />
                    </MDBox>
                    {Object.entries(
                      property.criteria
                        .filter((criterion) => {
                          if (!criteriaFilter.trim()) return true;
                          const q = criteriaFilter.toLowerCase().trim();
                          const nameStr =
                            typeof criterion.name === "object" && criterion.name
                              ? criterion.name.en || criterion.name.ar || ""
                              : criterion.name || "";
                          const categoryStr =
                            typeof criterion.category === "object" && criterion.category
                              ? criterion.category.en || criterion.category.ar || ""
                              : criterion.category || "";
                          return (
                            nameStr.toLowerCase().includes(q) ||
                            categoryStr.toLowerCase().includes(q)
                          );
                        })
                        .reduce((acc, criterion) => {
                          const category =
                            typeof criterion.category === "object" && criterion.category?.en
                              ? criterion.category.en
                              : typeof criterion.category === "string"
                              ? criterion.category
                              : "General";
                          if (!acc[category]) acc[category] = [];
                          acc[category].push(criterion);
                          return acc;
                        }, {})
                    ).map(([category, criteria]) => (
                      <MDBox key={category} mb={3}>
                        <MDTypography
                          variant="subtitle1"
                          color="info.main"
                          fontWeight="bold"
                          display="block"
                          mb={1.5}
                        >
                          {category}
                        </MDTypography>
                        <Grid container spacing={2}>
                          {criteria.map((criterion, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                              <Card
                                sx={{
                                  p: 2,
                                  border: criterion.verified ? "2px solid" : "1px solid",
                                  borderColor: criterion.verified ? "success.main" : "grey.300",
                                  bgcolor: criterion.verified
                                    ? "rgba(76, 175, 80, 0.05)"
                                    : "grey.50",
                                  height: "100%",
                                }}
                              >
                                <MDBox display="flex" alignItems="center" mb={1}>
                                  <Icon
                                    sx={{
                                      mr: 1,
                                      color: criterion.verified ? "success.main" : "grey.400",
                                      fontSize: "1.2rem",
                                    }}
                                  >
                                    {criterion.verified ? "check_circle" : "cancel"}
                                  </Icon>
                                  <MDTypography
                                    variant="body2"
                                    fontWeight="medium"
                                    color={criterion.verified ? "success.dark" : "text.secondary"}
                                  >
                                    {typeof criterion.name === "object" && criterion.name?.en
                                      ? criterion.name.en
                                      : typeof criterion.name === "string"
                                      ? criterion.name
                                      : "N/A"}
                                    {criterion.quantifiable && criterion.quantity != null && (
                                      <MDTypography
                                        component="span"
                                        variant="caption"
                                        color="info.main"
                                        sx={{ ml: 0.5 }}
                                      >
                                        ({criterion.quantity} pcs)
                                      </MDTypography>
                                    )}
                                  </MDTypography>
                                </MDBox>
                                {criterion.notes && (
                                  <MDBox mt={1.5} p={1.5} bgcolor="info.light" borderRadius={1}>
                                    <MDTypography
                                      variant="caption"
                                      fontWeight="bold"
                                      color="info.dark"
                                      display="block"
                                      mb={0.5}
                                    >
                                      Inspector Comment:
                                    </MDTypography>
                                    <MDTypography
                                      variant="caption"
                                      color="text"
                                      sx={{ whiteSpace: "pre-wrap" }}
                                    >
                                      {typeof criterion.notes === "object" && criterion.notes?.en
                                        ? criterion.notes.en
                                        : typeof criterion.notes === "string"
                                        ? criterion.notes
                                        : ""}
                                    </MDTypography>
                                  </MDBox>
                                )}
                                {criterion.verifiedAt && (
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    mt={1}
                                  >
                                    Verified: {new Date(criterion.verifiedAt).toLocaleDateString()}
                                  </MDTypography>
                                )}
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      </MDBox>
                    ))}
                    <MDBox mt={2} p={2} bgcolor="info.light" borderRadius={2}>
                      <MDTypography variant="body2" fontWeight="medium" color="info.dark">
                        <Icon sx={{ verticalAlign: "middle", mr: 0.5, fontSize: "1.2rem" }}>
                          info
                        </Icon>
                        Verified: {property.criteria.filter((c) => c.verified).length} /{" "}
                        {property.criteria.length} criteria
                      </MDTypography>
                    </MDBox>
                  </Card>
                ) : (
                  <MDBox textAlign="center" py={6}>
                    <Icon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}>checklist</Icon>
                    <MDTypography variant="h6" color="text.secondary" mb={1}>
                      No Criteria Available
                    </MDTypography>
                    <MDTypography variant="body2" color="text.secondary">
                      No criteria were specified for this property.
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            )}
          </MDBox>
        </Card>

        {/* Action Buttons at Bottom */}
        <MDBox mt={3} display="flex" justifyContent="flex-end" gap={2}>
          <MDButton
            variant="outlined"
            color="info"
            onClick={() => navigate("/super-admin/inspections")}
            startIcon={<Icon>arrow_back</Icon>}
          >
            Back to List
          </MDButton>
          {property && property.status === "Preparation" && (
            <MDButton
              variant="gradient"
              color="success"
              onClick={async () => {
                if (
                  window.confirm(
                    "Are you sure you want to activate this property? It will be available for booking immediately."
                  )
                ) {
                  try {
                    setSubmitting(true);
                    setMessage({ type: "", text: "" });
                    const response = await superAdminAPI.activateProperty(
                      property._id || property.id
                    );
                    if (response.success) {
                      setMessage({
                        type: "success",
                        text:
                          response.message ||
                          "Property activated successfully. Property is now available for booking.",
                      });
                      await fetchPropertyDetails();
                      setTimeout(() => {
                        navigate("/super-admin/inspections");
                      }, 2000);
                    }
                  } catch (error) {
                    console.error("Error activating property:", error);
                    setMessage({
                      type: "error",
                      text: error.message || "Failed to activate property. Please try again.",
                    });
                  } finally {
                    setSubmitting(false);
                  }
                }
              }}
              disabled={submitting}
              startIcon={<Icon>play_arrow</Icon>}
            >
              {submitting ? "Activating..." : "Activate Property"}
            </MDButton>
          )}
        </MDBox>
      </MDBox>

      {/* Approve/Reject Action Dialog */}
      <Dialog
        open={actionDialogOpen}
        onClose={() => !submitting && setActionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {property?.status === "Second_Inspection_Completed"
            ? actionType === "approve"
              ? "Approve Second Inspection"
              : "Reject Second Inspection"
            : "Reject Property"}
        </DialogTitle>
        <DialogContent>
          {property && (
            <MDBox>
              <MDBox mb={2}>
                <MDTypography variant="body2" color="text" mb={2}>
                  {property.status === "Second_Inspection_Completed" && actionType === "approve"
                    ? "Approve this second inspection. The property will move to Photo Shooting."
                    : property.status === "Second_Inspection_Completed" && actionType === "reject"
                    ? "Reject this second inspection. Please provide a reason."
                    : "You are about to reject this property. Please provide a reason for the rejection."}
                </MDTypography>
                <MDBox p={2} borderRadius="lg" bgColor="grey.100" mb={2}>
                  <MDTypography variant="subtitle2" fontWeight="medium">
                    {property.title?.en || property.title || "N/A"}
                  </MDTypography>
                  {property.title?.ar && (
                    <MDTypography
                      variant="subtitle2"
                      fontWeight="medium"
                      sx={{ direction: "rtl", textAlign: "right" }}
                    >
                      {property.title.ar}
                    </MDTypography>
                  )}
                  <MDTypography variant="body2" color="text">
                    {property.address?.en || property.address || "N/A"},{" "}
                    {property.city?.en || property.city || ""}
                  </MDTypography>
                  {(property.address?.ar || property.city?.ar) && (
                    <MDTypography
                      variant="body2"
                      color="text.secondary"
                      sx={{ direction: "rtl", textAlign: "right" }}
                    >
                      {property.address?.ar || ""}, {property.city?.ar || ""}
                    </MDTypography>
                  )}
                </MDBox>
              </MDBox>

              {actionType === "approve" && property.status === "Second_Inspection_Completed" ? (
                <MDTypography variant="body2" color="text.secondary"></MDTypography>
              ) : actionType === "approve" ? (
                <MDBox>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Price per Night (EGP) *"
                        placeholder="Enter price per night"
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                        disabled={submitting}
                        required
                        error={price && parseFloat(price) <= 0}
                        helperText={
                          price && parseFloat(price) <= 0
                            ? "Price must be greater than 0"
                            : "Set the nightly rental price for this property"
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon>attach_money</Icon>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Profit Percentage (%) *"
                        placeholder="Enter profit percentage (0-100)"
                        value={profitPercentage}
                        onChange={(e) => setProfitPercentage(e.target.value)}
                        disabled={submitting}
                        required
                        error={
                          profitPercentage &&
                          (parseFloat(profitPercentage) < 0 || parseFloat(profitPercentage) > 100)
                        }
                        helperText={
                          profitPercentage &&
                          (parseFloat(profitPercentage) < 0 || parseFloat(profitPercentage) > 100)
                            ? "Percentage must be between 0 and 100"
                            : "Percentage of revenue taken as profit by the organization"
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon>percent</Icon>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    {price &&
                      profitPercentage &&
                      parseFloat(price) > 0 &&
                      parseFloat(profitPercentage) >= 0 &&
                      parseFloat(profitPercentage) <= 100 && (
                        <Grid item xs={12}>
                          <MDBox p={2} borderRadius={1} bgcolor="info.light">
                            <MDTypography
                              variant="caption"
                              fontWeight="bold"
                              color="info.dark"
                              display="block"
                              mb={1}
                            >
                              Revenue Breakdown:
                            </MDTypography>
                            <MDTypography variant="body2" color="text" mb={0.5}>
                              Total Price: <strong>{parseFloat(price).toFixed(2)} EGP</strong>
                            </MDTypography>
                            <MDTypography variant="body2" color="text" mb={0.5}>
                              Organization Profit ({profitPercentage}%):{" "}
                              <strong style={{ color: "#e91e63" }}>
                                {((parseFloat(price) * parseFloat(profitPercentage)) / 100).toFixed(
                                  2
                                )}{" "}
                                EGP
                              </strong>
                            </MDTypography>
                            <MDTypography variant="body2" color="text">
                              Owner Earnings:{" "}
                              <strong style={{ color: "#4caf50" }}>
                                {(
                                  parseFloat(price) -
                                  (parseFloat(price) * parseFloat(profitPercentage)) / 100
                                ).toFixed(2)}{" "}
                                EGP
                              </strong>
                            </MDTypography>
                          </MDBox>
                        </Grid>
                      )}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Insurance Price (EGP)"
                        placeholder="Enter insurance price (optional)"
                        value={insurancePrice}
                        onChange={(e) => setInsurancePrice(e.target.value)}
                        disabled={submitting}
                        error={insurancePrice && parseFloat(insurancePrice) < 0}
                        helperText={
                          insurancePrice && parseFloat(insurancePrice) < 0
                            ? "Insurance price must be greater than or equal to 0"
                            : "Fixed insurance price for this property (optional)"
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon>security</Icon>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Maintenance Cost Percentage (%)"
                        placeholder="Enter maintenance cost percentage (0-100)"
                        value={maintenanceCostPercentage}
                        onChange={(e) => setMaintenanceCostPercentage(e.target.value)}
                        disabled={submitting}
                        error={
                          maintenanceCostPercentage &&
                          (parseFloat(maintenanceCostPercentage) < 0 ||
                            parseFloat(maintenanceCostPercentage) > 100)
                        }
                        helperText={
                          maintenanceCostPercentage &&
                          (parseFloat(maintenanceCostPercentage) < 0 ||
                            parseFloat(maintenanceCostPercentage) > 100)
                            ? "Percentage must be between 0 and 100"
                            : "Expected percentage of maintenance cost for this property (optional)"
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon>build</Icon>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Allowed Person Number"
                        placeholder="Max guests allowed (e.g. 6)"
                        value={maxGuests}
                        onChange={(e) => setMaxGuests(e.target.value)}
                        disabled={submitting}
                        error={
                          maxGuests &&
                          (parseInt(maxGuests, 10) < 1 || isNaN(parseInt(maxGuests, 10)))
                        }
                        helperText={
                          maxGuests &&
                          (parseInt(maxGuests, 10) < 1 || isNaN(parseInt(maxGuests, 10)))
                            ? "Must be at least 1"
                            : "Maximum number of guests allowed (optional)"
                        }
                        inputProps={{ min: 1 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon>people</Icon>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Extra Fees per Person (EGP)"
                        placeholder="Extra charge per person (e.g. 100)"
                        value={extraFeesPerPerson}
                        onChange={(e) => setExtraFeesPerPerson(e.target.value)}
                        disabled={submitting}
                        error={
                          extraFeesPerPerson &&
                          (parseFloat(extraFeesPerPerson) < 0 ||
                            isNaN(parseFloat(extraFeesPerPerson)))
                        }
                        helperText={
                          extraFeesPerPerson &&
                          (parseFloat(extraFeesPerPerson) < 0 ||
                            isNaN(parseFloat(extraFeesPerPerson)))
                            ? "Must be 0 or greater"
                            : "Additional fee per person in EGP (optional)"
                        }
                        inputProps={{ min: 0 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon>attach_money</Icon>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Early Check-in Fee (%)"
                        placeholder="Percentage of price per night (0–100)"
                        value={earlyCheckInFeePercentage}
                        onChange={(e) => setEarlyCheckInFeePercentage(e.target.value)}
                        disabled={submitting}
                        error={
                          earlyCheckInFeePercentage &&
                          (parseFloat(earlyCheckInFeePercentage) < 0 ||
                            parseFloat(earlyCheckInFeePercentage) > 100 ||
                            isNaN(parseFloat(earlyCheckInFeePercentage)))
                        }
                        helperText={
                          earlyCheckInFeePercentage &&
                          (parseFloat(earlyCheckInFeePercentage) < 0 ||
                            parseFloat(earlyCheckInFeePercentage) > 100 ||
                            isNaN(parseFloat(earlyCheckInFeePercentage)))
                            ? "Must be between 0 and 100"
                            : "Fee as % of price per night for early check-in (optional)"
                        }
                        inputProps={{ min: 0, max: 100, step: 0.5 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon>schedule</Icon>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Late Checkout Fee (%)"
                        placeholder="Percentage of price per night (0–100)"
                        value={lateCheckOutFeePercentage}
                        onChange={(e) => setLateCheckOutFeePercentage(e.target.value)}
                        disabled={submitting}
                        error={
                          lateCheckOutFeePercentage &&
                          (parseFloat(lateCheckOutFeePercentage) < 0 ||
                            parseFloat(lateCheckOutFeePercentage) > 100 ||
                            isNaN(parseFloat(lateCheckOutFeePercentage)))
                        }
                        helperText={
                          lateCheckOutFeePercentage &&
                          (parseFloat(lateCheckOutFeePercentage) < 0 ||
                            parseFloat(lateCheckOutFeePercentage) > 100 ||
                            isNaN(parseFloat(lateCheckOutFeePercentage)))
                            ? "Must be between 0 and 100"
                            : "Fee as % of price per night for late checkout (optional)"
                        }
                        inputProps={{ min: 0, max: 100, step: 0.5 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon>schedule</Icon>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth required>
                        <InputLabel>Check-In/Check-Out Agent *</InputLabel>
                        <Select
                          value={checkInOutAgentId}
                          onChange={(e) => setCheckInOutAgentId(e.target.value)}
                          label="Check-In/Check-Out Agent *"
                          disabled={submitting || loadingAgents}
                          error={!checkInOutAgentId}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              minHeight: "44px",
                            },
                            "& .MuiSelect-select": {
                              minHeight: "44px",
                              display: "flex",
                              alignItems: "center",
                            },
                          }}
                        >
                          {checkInOutAgents.map((agent) => (
                            <MenuItem key={agent._id || agent.id} value={agent._id || agent.id}>
                              {agent.fullName} ({agent.email})
                            </MenuItem>
                          ))}
                        </Select>
                        {loadingAgents && (
                          <MDBox display="flex" alignItems="center" mt={1}>
                            <CircularProgress size={16} sx={{ mr: 1 }} />
                            <MDTypography variant="caption" color="text.secondary">
                              Loading agents...
                            </MDTypography>
                          </MDBox>
                        )}
                        {!loadingAgents && checkInOutAgents.length === 0 && (
                          <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                            No check-in/check-out agents available. Please create an agent first.
                          </MDTypography>
                        )}
                        {!checkInOutAgentId && (
                          <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                            Check-in/check-out agent is required to approve and list the property.
                          </MDTypography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        type="date"
                        label="Expected Ready Date *"
                        value={expectedReadyDate}
                        onChange={(e) => setExpectedReadyDate(e.target.value)}
                        disabled={submitting}
                        required
                        InputLabelProps={{
                          shrink: true,
                        }}
                        error={
                          expectedReadyDate &&
                          new Date(expectedReadyDate) < new Date(new Date().setHours(0, 0, 0, 0))
                        }
                        helperText={
                          expectedReadyDate &&
                          new Date(expectedReadyDate) < new Date(new Date().setHours(0, 0, 0, 0))
                            ? "Date must be today or in the future"
                            : "Select when the property will be ready for booking. Property will be in Preparation phase until this date."
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon>calendar_today</Icon>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            minHeight: "44px",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Approval Notes (Optional)"
                        placeholder="Add any notes about this approval..."
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        disabled={submitting}
                      />
                    </Grid>
                  </Grid>
                </MDBox>
              ) : (
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Rejection Reason *"
                  placeholder="Please provide a detailed reason for rejecting this property..."
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  disabled={submitting}
                  required
                  error={!rejectReason.trim() && rejectReason.length > 0}
                  helperText={
                    !rejectReason.trim() && rejectReason.length > 0
                      ? "Rejection reason is required"
                      : ""
                  }
                />
              )}
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setActionDialogOpen(false);
              setNotes("");
              setRejectReason("");
              setPrice("");
              setProfitPercentage("");
              setInsurancePrice("");
              setMaintenanceCostPercentage("");
              setMaxGuests("");
              setExtraFeesPerPerson("");
              setEarlyCheckInFeePercentage("");
              setCheckInOutAgentId("");
              setExpectedReadyDate("");
            }}
            disabled={submitting}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleSubmitAction}
            variant="gradient"
            color={actionType === "approve" ? "success" : "error"}
            disabled={
              submitting ||
              (actionType === "reject" && !rejectReason.trim()) ||
              (actionType === "approve" &&
                property?.status !== "Second_Inspection_Completed" &&
                (!price ||
                  !profitPercentage ||
                  !checkInOutAgentId ||
                  !expectedReadyDate ||
                  parseFloat(price) <= 0 ||
                  parseFloat(profitPercentage) < 0 ||
                  parseFloat(profitPercentage) > 100))
            }
          >
            {submitting
              ? actionType === "approve"
                ? "Approving..."
                : "Rejecting..."
              : actionType === "approve" && property?.status === "Second_Inspection_Completed"
              ? "Approve Second Inspection"
              : actionType === "approve"
              ? "Approve & Set Preparation Phase"
              : "Reject"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Approve to Inspection Offer Dialog (First Inspection) */}
      <Dialog
        open={approveToOfferDialogOpen}
        onClose={() => !submittingApproveToOffer && setApproveToOfferDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon color="success">check_circle</Icon>
            Approve to Inspection Offer
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" color="text" mb={2}>
            A draft contract will be generated and sent to the property owner. The property will
            move to <strong>Inspection Offer</strong> phase so the owner can approve, decline, or
            request a meeting.
          </MDTypography>
          {property && (
            <MDBox p={2} borderRadius={2} bgcolor="grey.100">
              <MDTypography variant="subtitle2" fontWeight="medium">
                {property.title?.en || property.title || "N/A"}
              </MDTypography>
              <MDTypography variant="body2" color="text">
                {property.address?.en || property.address || "N/A"},{" "}
                {property.city?.en || property.city || ""}
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => !submittingApproveToOffer && setApproveToOfferDialogOpen(false)}
            disabled={submittingApproveToOffer}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="success"
            onClick={handleApproveToInspectionOffer}
            disabled={submittingApproveToOffer}
            startIcon={
              submittingApproveToOffer ? <CircularProgress size={20} /> : <Icon>check_circle</Icon>
            }
          >
            {submittingApproveToOffer ? "Approving..." : "Approve to Inspection Offer"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Send Missing Items Email Dialog */}
      <Dialog
        open={missingItemsDialogOpen}
        onClose={() => !submittingMissingItems && setMissingItemsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon color="warning">email</Icon>
            Send Missing Items Email
          </MDBox>
        </DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="body2" color="text.secondary" mb={2}>
            Send a PDF email to the property owner with missing items, expected dates, and optional
            income projection.
          </MDTypography>
          {(missingItemsData.items || []).map((item, idx) => (
            <MDBox key={idx} display="flex" gap={1} mb={2} flexWrap="wrap" alignItems="flex-start">
              <TextField
                label="Item name"
                value={item.name || ""}
                onChange={(e) => {
                  const items = [...(missingItemsData.items || [])];
                  items[idx] = { ...items[idx], name: e.target.value };
                  setMissingItemsData({ ...missingItemsData, items });
                }}
                placeholder="e.g. AC"
                disabled={submittingMissingItems}
                size="small"
                sx={{ minWidth: 120, flex: 1 }}
              />
              <TextField
                type="number"
                label="Qty"
                value={item.quantity !== undefined && item.quantity !== "" ? item.quantity : ""}
                onChange={(e) => {
                  const items = [...(missingItemsData.items || [])];
                  items[idx] = { ...items[idx], quantity: e.target.value };
                  setMissingItemsData({ ...missingItemsData, items });
                }}
                placeholder="1"
                disabled={submittingMissingItems}
                inputProps={{ min: 1 }}
                size="small"
                sx={{ minWidth: 70 }}
              />
              <TextField
                type="number"
                label="Price (EGP)"
                value={item.price !== undefined && item.price !== "" ? item.price : ""}
                onChange={(e) => {
                  const items = [...(missingItemsData.items || [])];
                  items[idx] = { ...items[idx], price: e.target.value };
                  setMissingItemsData({ ...missingItemsData, items });
                }}
                placeholder="0"
                disabled={submittingMissingItems}
                size="small"
                sx={{ minWidth: 100 }}
              />
              <IconButton
                color="error"
                size="small"
                onClick={() => {
                  const items = (missingItemsData.items || []).filter((_, i) => i !== idx);
                  setMissingItemsData({
                    ...missingItemsData,
                    items: items.length ? items : [{ name: "", quantity: 1, price: "" }],
                  });
                }}
                disabled={submittingMissingItems || (missingItemsData.items || []).length <= 1}
              >
                <Icon>delete</Icon>
              </IconButton>
            </MDBox>
          ))}
          <MDButton
            variant="outlined"
            color="secondary"
            size="small"
            startIcon={<Icon>add</Icon>}
            onClick={() =>
              setMissingItemsData({
                ...missingItemsData,
                items: [...(missingItemsData.items || []), { name: "", quantity: 1, price: "" }],
              })
            }
            disabled={submittingMissingItems}
            sx={{ mb: 2 }}
          >
            Add item
          </MDButton>
          <TextField
            fullWidth
            type="date"
            label="Expected completion date"
            value={missingItemsData.expectedDate || ""}
            onChange={(e) =>
              setMissingItemsData({ ...missingItemsData, expectedDate: e.target.value })
            }
            InputLabelProps={{ shrink: true }}
            disabled={submittingMissingItems}
            size="small"
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            type="date"
            label="Expected date from approval to listing"
            value={missingItemsData.expectedDateApprovalToListing || ""}
            onChange={(e) =>
              setMissingItemsData({
                ...missingItemsData,
                expectedDateApprovalToListing: e.target.value,
              })
            }
            InputLabelProps={{ shrink: true }}
            disabled={submittingMissingItems}
            size="small"
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Custom message (optional)"
            value={missingItemsData.customMessage || ""}
            onChange={(e) =>
              setMissingItemsData({ ...missingItemsData, customMessage: e.target.value })
            }
            disabled={submittingMissingItems}
            size="small"
            sx={{ mb: 2 }}
          />
          <MDTypography variant="caption" color="text.secondary" display="block" mb={1}>
            Income projection (optional)
          </MDTypography>
          <MDBox display="flex" gap={1} flexWrap="wrap" mb={2}>
            <TextField
              type="number"
              label="Price/day (EGP)"
              value={missingItemsData.pricePerDay ?? ""}
              onChange={(e) =>
                setMissingItemsData({ ...missingItemsData, pricePerDay: e.target.value })
              }
              disabled={submittingMissingItems}
              size="small"
              sx={{ minWidth: 120 }}
            />
            <TextField
              type="number"
              label="Occupancy %"
              value={missingItemsData.occupancyRate ?? ""}
              onChange={(e) =>
                setMissingItemsData({ ...missingItemsData, occupancyRate: e.target.value })
              }
              disabled={submittingMissingItems}
              inputProps={{ min: 0, max: 100 }}
              size="small"
              sx={{ minWidth: 100 }}
            />
            <TextField
              type="number"
              label="Company %"
              value={missingItemsData.companyPercentage ?? ""}
              onChange={(e) =>
                setMissingItemsData({ ...missingItemsData, companyPercentage: e.target.value })
              }
              disabled={submittingMissingItems}
              inputProps={{ min: 0, max: 100 }}
              size="small"
              sx={{ minWidth: 90 }}
            />
            <TextField
              type="number"
              label="Booking site %"
              value={missingItemsData.bookingWebsitePercentage ?? ""}
              onChange={(e) =>
                setMissingItemsData({
                  ...missingItemsData,
                  bookingWebsitePercentage: e.target.value,
                })
              }
              disabled={submittingMissingItems}
              inputProps={{ min: 0, max: 100 }}
              size="small"
              sx={{ minWidth: 100 }}
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => !submittingMissingItems && setMissingItemsDialogOpen(false)}
            disabled={submittingMissingItems}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="warning"
            onClick={handleSendMissingItems}
            disabled={submittingMissingItems}
            startIcon={submittingMissingItems ? <CircularProgress size={20} /> : <Icon>send</Icon>}
          >
            {submittingMissingItems ? "Sending..." : "Send Email"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default InspectionReview;
